import { LIMIT_SOURCES } from '@felix/domain';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionIcon,
  Alert,
  Button,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Controller, type FieldPath, type UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DayInput } from '../../inputs/DayInput.js';
import classes from './Condition.module.css';
import type { MeasurementPoint, MeasurementPointInput } from './condition-types.js';
import { dangerLabel, limitLabel, measureNumber } from './condition-view.js';
import { usePmsErrorText } from './EquipmentDrawer.js';
import pms from './Pms.module.css';
import work from './PmsWork.module.css';
import {
  emptyPointForm,
  type PointFormValues,
  pointFormOf,
  pointFormSchema,
  pointPatchOf,
  toPointInput,
} from './point-form.js';

export interface MeasurementPointEditorProps {
  /** `null`: kaydedilmemiş yeni iş (önce iş kaydedilir). */
  jobId: string | null;
  equipmentId: string;
  /** Geminin ya da ekipmanın noktaları; işin noktaları burada süzülür. */
  points: readonly MeasurementPoint[];
  can: { create: boolean; edit: boolean; delete: boolean };
  today: string;
  onCreate: (input: MeasurementPointInput) => Promise<unknown>;
  onUpdate: (point: MeasurementPoint, patch: Partial<MeasurementPointInput>) => Promise<unknown>;
  onRemove: (point: MeasurementPoint, reason: string) => Promise<unknown>;
}

/**
 * Job Card ▸ ölçüm noktaları (plan M7 Task 21a; PMS belgesi §11): işin noktaları, limitleri ve
 * dayanağı; ekleme ve düzenleme penceresi (RHF + Zod). Limitler bakım programının parçasıdır [UR
 * Z20 1.3.3, UR Z27 2.1.3]; limit değişikliği gerekçe ister [UR Z27 2.2.4]. Yazma gemide ve kurulum
 * devrindeki ofiste; etkinleşmiş gemi ofiste salt okunur (K1).
 */
export function MeasurementPointEditor(props: MeasurementPointEditorProps) {
  const { t, i18n } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`pms.condition.editor.${key}`, options);
  const language = i18n.resolvedLanguage ?? 'en';
  const [editing, setEditing] = useState<MeasurementPoint | 'new' | null>(null);
  const [removing, setRemoving] = useState<MeasurementPoint | null>(null);
  const own = props.points.filter((p) => p.deletedAt === null && p.jobDefinitionId === props.jobId);
  return (
    <fieldset className={pms.fieldset}>
      <legend>{k('title')}</legend>
      <p className={work.note}>{k('hint')}</p>
      {props.jobId === null ? (
        <p className={work.note}>{k('saveJobFirst')}</p>
      ) : (
        <>
          {own.length === 0 ? (
            <p className={work.note}>{t('pms.condition.points.none')}</p>
          ) : (
            <ul className={classes.pointList}>
              {own.map((point) => {
                const danger = dangerLabel(point, point.unit, language);
                return (
                  <li key={point.id}>
                    <span className={classes.pointMain}>
                      <strong>{point.name}</strong>
                      <span className={classes.sub}>
                        {limitLabel(point, point.unit, language)}
                        {danger !== '—'
                          ? ` · ${t('pms.condition.points.danger', { limit: danger })}`
                          : ''}
                      </span>
                      <span className={classes.sub}>
                        {t(`pms.condition.limitSources.${point.limitSource}`, {
                          defaultValue: point.limitSource,
                        })}
                        {point.provider ? ` · ${point.provider}` : ''}
                        {point.baselineValue !== null
                          ? ` · ${t('pms.condition.points.baseline', {
                              value: measureNumber(language, point.baselineValue),
                              unit: point.unit,
                            })}`
                          : ''}
                      </span>
                      {!point.autoDefect ? (
                        <span className={classes.sub}>
                          {t('pms.condition.points.autoDefectOff', {
                            reason: point.autoDefectOffReason ?? '—',
                          })}
                        </span>
                      ) : null}
                    </span>
                    <span className={classes.pointSide}>
                      {props.can.edit ? (
                        <ActionIcon
                          variant="subtle"
                          aria-label={k('edit', { name: point.name })}
                          onClick={() => setEditing(point)}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                      ) : null}
                      {props.can.delete ? (
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label={k('remove', { name: point.name })}
                          onClick={() => setRemoving(point)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          {props.can.create ? (
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => setEditing('new')}
            >
              {k('add')}
            </Button>
          ) : null}
        </>
      )}
      <PointDialog
        opened={editing !== null}
        existing={editing === 'new' ? null : editing}
        equipmentId={props.equipmentId}
        jobId={props.jobId}
        today={props.today}
        onClose={() => setEditing(null)}
        onSubmit={async (values, existing) => {
          if (existing) await props.onUpdate(existing, pointPatchOf(existing, values));
          else await props.onCreate(toPointInput(values));
          setEditing(null);
        }}
      />
      <RemovePointDialog
        point={removing}
        onClose={() => setRemoving(null)}
        onConfirm={async (point, reason) => {
          await props.onRemove(point, reason);
          setRemoving(null);
        }}
      />
    </fieldset>
  );
}

function PointDialog({
  opened,
  existing,
  equipmentId,
  jobId,
  today,
  onClose,
  onSubmit,
}: {
  opened: boolean;
  existing: MeasurementPoint | null;
  equipmentId: string;
  jobId: string | null;
  today: string;
  onClose: () => void;
  onSubmit: (values: PointFormValues, existing: MeasurementPoint | null) => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = usePmsErrorText();
  const [error, setError] = useState<unknown>(null);
  const form = useForm<PointFormValues>({
    resolver: zodResolver(pointFormSchema({ today, ...(existing ? { existing } : {}) })),
    defaultValues: emptyPointForm(equipmentId, jobId),
  });
  useEffect(() => {
    if (!opened) return;
    setError(null);
    form.reset(existing ? pointFormOf(existing) : emptyPointForm(equipmentId, jobId));
  }, [opened, existing, equipmentId, jobId, form]);
  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await onSubmit(values, existing);
    } catch (failure) {
      setError(failure);
    }
  });
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      title={
        existing
          ? t('pms.condition.editor.editTitle', { name: existing.name })
          : t('pms.condition.editor.newTitle')
      }
      closeButtonProps={{ 'aria-label': t('pms.drawer.close') }}
    >
      <form
        onSubmit={(event) => {
          // İç içe form: Job Card formu gönderilmez
          event.stopPropagation();
          void submit(event);
        }}
        noValidate
      >
        <Stack gap="sm">
          <PointFormFields form={form} existing={existing} today={today} />
          {error ? (
            <Alert color="red" icon={<IconAlertCircle size={16} />} role="alert">
              {errorText(error)}
            </Alert>
          ) : null}
          <div className={work.dialogActions}>
            <Button variant="default" onClick={onClose}>
              {t('pms.drawer.cancel')}
            </Button>
            <Button type="submit" loading={form.formState.isSubmitting}>
              {t('pms.drawer.save')}
            </Button>
          </div>
        </Stack>
      </form>
    </Modal>
  );
}

/** Formun gövdesi (bileşen testi sunucu çizimiyle). */
export function PointFormFields({
  form,
  existing,
  today,
}: {
  form: UseFormReturn<PointFormValues>;
  existing: MeasurementPoint | null;
  today: string;
}) {
  const { t, i18n } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`pms.condition.editor.${key}`, options);
  const language = i18n.resolvedLanguage ?? 'en';
  const fieldError = (name: FieldPath<PointFormValues>) => {
    const message = form.formState.errors[name as keyof PointFormValues]?.message;
    return message
      ? t(`pms.errors.${message}`, {
          defaultValue: t(`pms.fieldErrors.${message}`, {
            defaultValue: t('pms.errors.VALIDATION_FAILED'),
          }),
        })
      : undefined;
  };
  const number = (
    name: 'nominal' | 'alertLow' | 'alertHigh' | 'dangerLow' | 'dangerHigh' | 'baselineValue',
    label: string,
  ) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => (
        <NumberInput
          label={label}
          decimalScale={4}
          value={field.value}
          onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
          onBlur={field.onBlur}
          error={fieldError(name)}
        />
      )}
    />
  );
  const flag = (name: 'lowInclusive' | 'highInclusive' | 'autoDefect', label: string) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => (
        <Switch
          label={label}
          checked={field.value}
          onChange={(event) => field.onChange(event.currentTarget.checked)}
        />
      )}
    />
  );
  const autoDefect = form.watch('autoDefect');
  const source = form.watch('limitSource');
  return (
    <Stack gap="sm">
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <TextInput
          label={k('name')}
          required
          {...form.register('name')}
          error={fieldError('name')}
        />
        <TextInput
          label={k('unit')}
          required
          readOnly={existing?.last != null}
          {...form.register('unit')}
          error={fieldError('unit')}
        />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        {number('alertLow', k('alertLow'))}
        {number('alertHigh', k('alertHigh'))}
        {number('dangerLow', k('dangerLow'))}
        {number('dangerHigh', k('dangerHigh'))}
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        {flag('lowInclusive', k('lowInclusive'))}
        {flag('highInclusive', k('highInclusive'))}
      </SimpleGrid>
      <p className={work.note}>{k('inclusiveHint')}</p>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Controller
          control={form.control}
          name="limitSource"
          render={({ field }) => (
            <Select
              label={k('limitSource')}
              required
              data={LIMIT_SOURCES.map((value) => ({
                value,
                label: t(`pms.condition.limitSources.${value}`),
              }))}
              value={field.value}
              allowDeselect={false}
              onChange={(value) => field.onChange(value ?? 'oem')}
            />
          )}
        />
        <TextInput
          label={k('provider')}
          required={source === 'cm_provider'}
          {...form.register('provider')}
          error={fieldError('provider')}
        />
        {number('nominal', k('nominal'))}
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        {number('baselineValue', k('baselineValue'))}
        <Controller
          control={form.control}
          name="baselineTakenOn"
          render={({ field }) => (
            <DayInput
              label={k('baselineTakenOn')}
              value={field.value || null}
              onChange={(value) => field.onChange(value ?? '')}
              maxDate={today}
              clearable
              clearButtonProps={{ 'aria-label': t('pms.work.clear') }}
              error={fieldError('baselineTakenOn')}
            />
          )}
        />
      </SimpleGrid>
      {existing?.baselineOriginal != null ? (
        <p className={work.note}>
          {k('baselineOriginal', {
            value: measureNumber(language, existing.baselineOriginal),
            unit: existing.unit,
          })}
        </p>
      ) : null}
      {flag('autoDefect', k('autoDefect'))}
      {autoDefect ? null : (
        <Textarea
          label={k('autoDefectOffReason')}
          required
          autosize
          minRows={2}
          {...form.register('autoDefectOffReason')}
          error={fieldError('autoDefectOffReason')}
        />
      )}
      {existing ? (
        <Textarea
          label={k('changeReason')}
          description={k('changeReasonHint')}
          autosize
          minRows={2}
          {...form.register('changeReason')}
          error={fieldError('changeReason')}
        />
      ) : null}
    </Stack>
  );
}

function RemovePointDialog({
  point,
  onClose,
  onConfirm,
}: {
  point: MeasurementPoint | null;
  onClose: () => void;
  onConfirm: (point: MeasurementPoint, reason: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const errorText = usePmsErrorText();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: başka nokta açılınca form sıfırlanır
  useEffect(() => {
    setReason('');
    setError(null);
  }, [point]);
  return (
    <Modal
      opened={point !== null}
      onClose={onClose}
      title={t('pms.condition.editor.removeTitle')}
      closeButtonProps={{ 'aria-label': t('pms.drawer.close') }}
    >
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!point || reason.trim() === '') return;
          setBusy(true);
          setError(null);
          try {
            await onConfirm(point, reason.trim());
          } catch (failure) {
            setError(failure);
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className={work.dialogBody}>{t('pms.condition.editor.removeBody')}</p>
        <Textarea
          label={t('pms.condition.editor.removeReason')}
          required
          autosize
          minRows={2}
          maxLength={500}
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
        />
        {error ? (
          <p className={work.error} role="alert">
            {errorText(error)}
          </p>
        ) : null}
        <div className={work.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {t('pms.drawer.cancel')}
          </Button>
          <Button type="submit" color="red" loading={busy} disabled={reason.trim() === ''}>
            {t('pms.condition.editor.removeTitle')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
